import React from 'react';

/**
 * Section 7 : Situation psychologique et sociale
 */
export const AssessmentSection7_Psychologie = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange('psychological', {
      ...data,
      [field]: value
    });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = data?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    handleChange(field, newArray);
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">SITUATION PSYCHOLOGIQUE ET SOCIALE</h3>

      <div className="assessment-form-group">
        <label>Comment vous sentez-vous moralement ?</label>
        <select
          className="assessment-form-control"
          value={data?.mood || ''}
          onChange={(e) => handleChange('mood', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="bien">Bien / Serein(e)</option>
          <option value="plutot-bien">Plutôt bien</option>
          <option value="anxieux">Anxieux(se) / Inquiet(ète)</option>
          <option value="triste">Triste / Déprimé(e)</option>
          <option value="tres-mal">Très mal</option>
        </select>
      </div>

      <div className="assessment-form-group">
        <label>Ressentez-vous (cochez toutes les cases applicables) :</label>
        <div className="assessment-checkbox-group">
          {['Solitude', 'Ennui', 'Anxiété/Angoisses', 'Tristesse persistante', 'Perte d\'intérêt', 'Troubles du sommeil'].map(feeling => (
            <label key={feeling} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.feelings?.includes(feeling) || false}
                onChange={() => handleArrayToggle('feelings', feeling)}
              />
              <span>{feeling}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="assessment-form-group">
        <label>Activités et loisirs (cochez toutes les cases applicables)</label>
        <div className="assessment-checkbox-group">
          {['Télévision/Radio', 'Lecture', 'Jeux', 'Travaux manuels', 'Jardinage', 'Promenade', 'Activités associatives', 'Informatique'].map(activity => (
            <label key={activity} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.activities?.includes(activity) || false}
                onChange={() => handleArrayToggle('activities', activity)}
              />
              <span>{activity}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="assessment-form-group">
        <label>Fréquence des activités</label>
        <select
          className="assessment-form-control"
          value={data?.activityFrequency || ''}
          onChange={(e) => handleChange('activityFrequency', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="quotidienne">Quotidienne</option>
          <option value="plusieurs-semaine">Plusieurs fois par semaine</option>
          <option value="occasionnelle">Occasionnelle</option>
          <option value="aucune">Je n'ai plus d'activités</option>
        </select>
      </div>
    </div>
  );
};

/**
 * Section 8 : Aides actuelles et besoins
 */
export const AssessmentSection8_Aides = ({ data, onChange }) => {
  const handleChange = (section, field, value) => {
    onChange('currentAids', {
      ...data,
      [section]: {
        ...data?.[section],
        [field]: value
      }
    });
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">AIDES ACTUELLES ET BESOINS</h3>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
        Aides à domicile
      </h4>

      <div className="assessment-form-group">
        <label>Bénéficiez-vous d'une aide à domicile ?</label>
        <div className="assessment-radio-group">
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="homeHelp"
              checked={data?.homeHelp?.has === false}
              onChange={() => handleChange('homeHelp', 'has', false)}
            />
            <span>Non</span>
          </label>
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="homeHelp"
              checked={data?.homeHelp?.has === true}
              onChange={() => handleChange('homeHelp', 'has', true)}
            />
            <span>Oui</span>
          </label>
        </div>
      </div>

      {data?.homeHelp?.has && (
        <>
          <div className="assessment-form-group">
            <label>Organisme</label>
            <input
              type="text"
              className="assessment-form-control"
              value={data?.homeHelp?.organization || ''}
              onChange={(e) => handleChange('homeHelp', 'organization', e.target.value)}
            />
          </div>

          <div className="assessment-grid-2">
            <div className="assessment-form-group">
              <label>Fréquence</label>
              <select
                className="assessment-form-control"
                value={data?.homeHelp?.frequency || ''}
                onChange={(e) => handleChange('homeHelp', 'frequency', e.target.value)}
              >
                <option value="">-- Sélectionner --</option>
                <option value="quotidienne">Quotidienne</option>
                <option value="plusieurs-semaine">Plusieurs fois/semaine</option>
                <option value="hebdomadaire">Hebdomadaire</option>
              </select>
            </div>

            <div className="assessment-form-group">
              <label>Volume horaire hebdomadaire</label>
              <input
                type="number"
                className="assessment-form-control"
                value={data?.homeHelp?.weeklyHours || ''}
                onChange={(e) => handleChange('homeHelp', 'weeklyHours', parseInt(e.target.value) || null)}
              />
            </div>
          </div>
        </>
      )}

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Aides financières
      </h4>

      <div className="assessment-form-group">
        <label>APA (Allocation Personnalisée d'Autonomie)</label>
        <div className="assessment-radio-group">
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="apa"
              checked={data?.financialAids?.apa?.has === false}
              onChange={() => handleChange('financialAids', 'apa', { ...data?.financialAids?.apa, has: false })}
            />
            <span>Non</span>
          </label>
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="apa"
              checked={data?.financialAids?.apa?.has === true}
              onChange={() => handleChange('financialAids', 'apa', { ...data?.financialAids?.apa, has: true })}
            />
            <span>Oui</span>
          </label>
        </div>
      </div>

      {data?.financialAids?.apa?.has && (
        <div className="assessment-grid-2">
          <div className="assessment-form-group">
            <label>GIR</label>
            <select
              className="assessment-form-control"
              value={data?.financialAids?.apa?.gir || ''}
              onChange={(e) => handleChange('financialAids', 'apa', { ...data?.financialAids?.apa, gir: e.target.value })}
            >
              <option value="">-- Sélectionner --</option>
              {[1, 2, 3, 4, 5, 6].map(gir => (
                <option key={gir} value={gir}>GIR {gir}</option>
              ))}
            </select>
          </div>

          <div className="assessment-form-group">
            <label>Montant mensuel (€)</label>
            <input
              type="number"
              className="assessment-form-control"
              value={data?.financialAids?.apa?.amount || ''}
              onChange={(e) => handleChange('financialAids', 'apa', { ...data?.financialAids?.apa, amount: parseFloat(e.target.value) || null })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Section 9 : Besoins et attentes
 */
export const AssessmentSection9_Besoins = ({ data, onChange }) => {
  const handleChange = (section, field, value) => {
    onChange('needs', {
      ...data,
      [section]: {
        ...data?.[section],
        [field]: value
      }
    });
  };

  const handleArrayToggle = (section, field, value) => {
    const currentArray = data?.[section]?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];

    onChange('needs', {
      ...data,
      [section]: {
        ...data?.[section],
        [field]: newArray
      }
    });
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">BESOINS ET ATTENTES</h3>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '16px' }}>
        Nature des prestations souhaitées
      </h4>

      <div className="assessment-form-group">
        <label>Aide à la personne</label>
        <div className="assessment-checkbox-group">
          {['Aide à la toilette', 'Aide à l\'habillage', 'Aide aux repas', 'Aide aux transferts', 'Présence/Surveillance', 'Garde de nuit'].map(service => (
            <label key={service} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.services?.personalCare?.includes(service) || false}
                onChange={() => handleArrayToggle('services', 'personalCare', service)}
              />
              <span>{service}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="assessment-form-group">
        <label>Entretien du logement</label>
        <div className="assessment-checkbox-group">
          {['Ménage', 'Lessive/Repassage', 'Changement des draps'].map(service => (
            <label key={service} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.services?.housekeeping?.includes(service) || false}
                onChange={() => handleArrayToggle('services', 'housekeeping', service)}
              />
              <span>{service}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="assessment-form-group">
        <label>Accompagnement extérieur</label>
        <div className="assessment-checkbox-group">
          {['Courses', 'Rendez-vous médicaux', 'Démarches administratives', 'Promenades'].map(service => (
            <label key={service} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.services?.accompaniment?.includes(service) || false}
                onChange={() => handleArrayToggle('services', 'accompaniment', service)}
              />
              <span>{service}</span>
            </label>
          ))}
        </div>
      </div>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Organisation souhaitée
      </h4>

      <div className="assessment-grid-2">
        <div className="assessment-form-group">
          <label>Fréquence d'intervention souhaitée</label>
          <select
            className="assessment-form-control"
            value={data?.schedule?.frequency || ''}
            onChange={(e) => handleChange('schedule', 'frequency', e.target.value)}
          >
            <option value="">-- Sélectionner --</option>
            <option value="quotidienne">Quotidienne</option>
            <option value="plusieurs-semaine">Plusieurs fois par semaine</option>
            <option value="hebdomadaire">Hebdomadaire</option>
            <option value="ponctuelle">Ponctuelle / Occasionnelle</option>
          </select>
        </div>

        <div className="assessment-form-group">
          <label>Volume horaire hebdomadaire estimé</label>
          <input
            type="number"
            className="assessment-form-control"
            placeholder="Heures/semaine"
            value={data?.schedule?.weeklyHours || ''}
            onChange={(e) => handleChange('schedule', 'weeklyHours', parseInt(e.target.value) || null)}
          />
        </div>
      </div>

      <div className="assessment-form-group">
        <label>Attentes principales vis-à-vis de l'intervenant</label>
        <textarea
          className="assessment-form-control"
          rows="4"
          value={data?.expectations || ''}
          onChange={(e) => onChange('needs', { ...data, expectations: e.target.value })}
        />
      </div>

      <div className="assessment-form-group">
        <label>Informations importantes à savoir</label>
        <textarea
          className="assessment-form-control"
          rows="4"
          placeholder="Habitudes de vie, craintes, souhaits particuliers, allergies, etc."
          value={data?.importantInfo || ''}
          onChange={(e) => onChange('needs', { ...data, importantInfo: e.target.value })}
        />
      </div>
    </div>
  );
};

/**
 * Section 10 : Projet d'accompagnement
 */
export const AssessmentSection10_Projet = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange('project', {
      ...data,
      [field]: value
    });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = data?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    handleChange(field, newArray);
  };

  const handleCaregiverChange = (field, value) => {
    onChange('project', {
      ...data,
      caregiver: {
        ...data?.caregiver,
        [field]: value
      }
    });
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">PROJET D'ACCOMPAGNEMENT</h3>

      <div className="assessment-form-group">
        <label>Quels sont vos objectifs principaux ?</label>
        <div className="assessment-checkbox-group">
          {['Maintien à domicile', 'Préservation de l\'autonomie', 'Sécurité et prévention', 'Soulagement de l\'aidant', 'Lien social', 'Aide ponctuelle après hospitalisation'].map(objective => (
            <label key={objective} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.objectives?.includes(objective) || false}
                onChange={() => handleArrayToggle('objectives', objective)}
              />
              <span>{objective}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="assessment-form-group">
        <label>Qu'est-ce qui est le plus important pour vous ?</label>
        <textarea
          className="assessment-form-control"
          rows="4"
          value={data?.mainPriority || ''}
          onChange={(e) => handleChange('mainPriority', e.target.value)}
        />
      </div>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Situation de l'aidant
      </h4>

      <div className="assessment-form-group">
        <label>Un proche vous aide-t-il au quotidien ?</label>
        <div className="assessment-radio-group">
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="hasCaregiver"
              checked={data?.caregiver?.hasCaregiver === false}
              onChange={() => handleCaregiverChange('hasCaregiver', false)}
            />
            <span>Non</span>
          </label>
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="hasCaregiver"
              checked={data?.caregiver?.hasCaregiver === true}
              onChange={() => handleCaregiverChange('hasCaregiver', true)}
            />
            <span>Oui</span>
          </label>
        </div>
      </div>

      {data?.caregiver?.hasCaregiver && (
        <>
          <div className="assessment-grid-2">
            <div className="assessment-form-group">
              <label>Identité de l'aidant</label>
              <input
                type="text"
                className="assessment-form-control"
                value={data?.caregiver?.identity || ''}
                onChange={(e) => handleCaregiverChange('identity', e.target.value)}
              />
            </div>

            <div className="assessment-form-group">
              <label>Lien de parenté</label>
              <input
                type="text"
                className="assessment-form-control"
                value={data?.caregiver?.relationship || ''}
                onChange={(e) => handleCaregiverChange('relationship', e.target.value)}
              />
            </div>
          </div>

          <div className="assessment-grid-2">
            <div className="assessment-form-group">
              <label>Fréquence de l'aide</label>
              <select
                className="assessment-form-control"
                value={data?.caregiver?.frequency || ''}
                onChange={(e) => handleCaregiverChange('frequency', e.target.value)}
              >
                <option value="">-- Sélectionner --</option>
                <option value="quotidienne">Quotidienne</option>
                <option value="hebdomadaire">Hebdomadaire</option>
                <option value="occasionnelle">Occasionnelle</option>
              </select>
            </div>

            <div className="assessment-form-group">
              <label>L'aidant ressent-il un épuisement ?</label>
              <select
                className="assessment-form-control"
                value={data?.caregiver?.burden || ''}
                onChange={(e) => handleCaregiverChange('burden', e.target.value)}
              >
                <option value="">-- Sélectionner --</option>
                <option value="non">Non</option>
                <option value="leger">Oui, léger</option>
                <option value="modere">Oui, modéré</option>
                <option value="important">Oui, important</option>
              </select>
            </div>
          </div>
        </>
      )}

      <div className="assessment-info-card" style={{ marginTop: '24px' }}>
        <p><strong>Félicitations !</strong> Vous avez complété toutes les sections du questionnaire d'évaluation.</p>
        <p>Veuillez cliquer sur "Terminer" pour sauvegarder et finaliser le questionnaire.</p>
      </div>
    </div>
  );
};
